import clsx from 'clsx';
import { useIntl } from 'react-intl';

import { SgComponentIcon } from '../../display/ComponentIcon';
import { SgButton } from '../SgButton';
import { useDatePickerCalendar } from './Calendar/CalendarContext';
import { useDatePickerConfig } from './contexts/ConfigContext';
import { useDatePickerActions } from './Dropdown/DropdownContext';

interface ClearButtonProps {
  showClearButton?: boolean;
  isHover?: boolean;
}

/**
 * CLEAR BUTTON COMPONENT
 * =====================
 *
 * Provides a clear button for the SgDatePicker that:
 * - Only appears when there are selected dates
 * - Shows/hides based on focus and open state for better UX
 * - Uses visibility instead of display for smooth transitions
 * - Integrates with the datePicker's context for consistent behavior
 * - Disabled when the datePicker is disabled or readonly
 */
export function ClearButton({ showClearButton, isHover }: ClearButtonProps) {
  const { formatMessage } = useIntl();
  const { size, disabled, readonly } = useDatePickerConfig();
  const { calendarState } = useDatePickerCalendar();
  const { onClear: handleClear } = useDatePickerActions();

  /**
   * VISIBILITY LOGIC
   * ================
   *
   * The button visibility is controlled by multiple factors:
   * 1. Must have selections (single or multiple mode)
   * 2. showClearButton prop must be true
   * 3. Component must be focused or dropdown open (UX improvement)
   * 4. Component must NOT be disabled or readonly
   */
  const hasSelection = calendarState.value;
  const shouldRender = hasSelection && showClearButton && !disabled && !readonly;
  const isVisible = shouldRender && isHover;

  if (!shouldRender) {
    return null;
  }

  const classNames = clsx('d-flex align-items-center align-self-center h-100');

  /**
   * Using visibility instead of display/conditional rendering provides:
   * - Smoother transitions
   * - Consistent layout (space is reserved)
   * - Better accessibility (screen readers can track state changes)
   */
  const btnClassName = clsx({
    invisible: !isVisible,
  });

  return (
    <div className={classNames}>
      <SgButton
        purpose="link"
        color="none"
        size={size}
        iconPosition="icon-only"
        className={btnClassName}
        onClick={handleClear}
        aria-label={formatMessage({ id: 'sgds.date.clearSelection' })}
      >
        <SgComponentIcon icon="cancel" size={size} data-e2e="cancel" />
      </SgButton>
    </div>
  );
}
